import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { fetchConfig, requestIdleCallback } from '@theme/utilities';

/**
 * Renders a "You may also like" panel inside the cart drawer, sourced from
 * Shopify's public `/recommendations/products.json` endpoint, and lets the shopper
 * add a recommended product straight from the drawer.
 *
 * Layout mirrors the reference structure (image, then a size selector + "AJOUTER"
 * button revealed on hover, then title / type / price) while using CAMÉLIA's own
 * tokens (secondary button style, theme typography and colours).
 *
 * @extends {Component}
 */
class CartRecommendations extends Component {
  /** @type {boolean} */
  #loaded = false;

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #retryTimer = null;

  /** @type {Array<Record<string, any>> | null} - last fetched products, kept to re-inject after a section morph */
  #products = null;

  /** @type {string | null} - the product id the cached products belong to */
  #productsForId = null;

  /**
   * Self-heal: the drawer is server-rendered with an empty list and hydrated on load,
   * which wipes the cards we inject client-side (without a connected/updated callback).
   * This observer detects when our list has been emptied and re-injects from cache.
   * @type {MutationObserver}
   */
  #healObserver = new MutationObserver(() => this.#healIfWiped());

  /**
   * Reload recommendations whenever the reference product changes (e.g. after an
   * item is added to the cart and `data-product-id` is updated by the server render).
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName !== 'data-product-id') continue;
      if (!this.#loaded) continue;

      this.#loadRecommendations();
      break;
    }
  });

  connectedCallback() {
    super.connectedCallback();

    this.#mutationObserver.observe(this, { attributes: true, attributeFilter: ['data-product-id'] });

    // Load as soon as the browser is idle. The element only exists when the cart is
    // non-empty and lives inside the (server-rendered) drawer, so a single small,
    // cacheable request here makes the panel ready whenever the drawer is opened —
    // on every page — instead of depending on a dialog:open event that can be missed
    // when the element is (re)created by a section morph after the first add-to-cart.
    requestIdleCallback(() => this.#loadRecommendations());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mutationObserver.disconnect();
    this.#healObserver.disconnect();
    this.#activeFetch?.abort();
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
  }

  /**
   * Re-inject the cached cards if a hydration/morph emptied our list.
   */
  #healIfWiped() {
    if (!this.#products?.length || this.#productsForId !== this.dataset.productId) return;
    const list = this.querySelector('[data-cart-reco-list]');
    if (list instanceof HTMLElement && list.children.length === 0) {
      this.#renderProducts(this.#products);
    }
  }

  /**
   * Called by the Section Rendering API (morph.js) after this element is re-rendered.
   * A morph (drawer hydration on load, or a cart update) resets our subtree to the
   * server HTML — an empty list — wiping the cards we injected client-side. Re-inject
   * them from cache so the panel survives, or (re)load if the reference product changed.
   */
  updatedCallback() {
    super.updatedCallback();

    const list = this.querySelector('[data-cart-reco-list]');
    if (!list) return;

    if (this.#products?.length && this.#productsForId === this.dataset.productId) {
      if (list.children.length === 0) this.#renderProducts(this.#products);
    } else if (!this.#activeFetch && !this.#retryTimer) {
      this.#loadRecommendations();
    }
  }

  /**
   * @param {number} [attempt] - current retry attempt (0-based)
   */
  async #loadRecommendations(attempt = 0) {
    const { productId, url, intent, limit } = this.dataset;
    if (!productId || !url) return;

    if (this.#retryTimer) {
      clearTimeout(this.#retryTimer);
      this.#retryTimer = null;
    }
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const requestUrl = `${url}.json?product_id=${encodeURIComponent(productId)}&limit=${
      limit || 4
    }&intent=${intent || 'related'}`;

    try {
      const response = await fetch(requestUrl, { signal: this.#activeFetch.signal });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      /** @type {{ products: Array<Record<string, any>> }} */
      const data = await response.json();
      const products = data.products || [];

      // Shopify's recommendations endpoint returns an empty array on the first (cold)
      // query for a product while it computes them asynchronously, then populates on
      // subsequent calls. Retry a few times before giving up so the panel reliably
      // appears whatever page the drawer is opened from.
      if (products.length === 0 && attempt < 5) {
        this.#activeFetch = null;
        this.#retryTimer = setTimeout(() => this.#loadRecommendations(attempt + 1), 1000);
        return;
      }

      this.#loaded = true;
      this.#renderProducts(products);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Cart recommendations error:', error);
      this.classList.add('hidden');
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {Array<Record<string, any>>} products
   */
  #renderProducts(products) {
    const list = this.querySelector('[data-cart-reco-list]');
    if (!(list instanceof HTMLElement)) return;

    const limit = Number(this.dataset.limit) || 4;
    const items = products.slice(0, limit);

    if (!items.length) {
      this.classList.add('hidden');
      return;
    }

    // Cache so we can re-inject the cards if a section morph later wipes our subtree.
    this.#products = products;
    this.#productsForId = this.dataset.productId ?? null;

    // Pause the self-heal observer around our own DOM writes to avoid re-triggering it.
    this.#healObserver.disconnect();
    this.classList.remove('hidden');
    list.replaceChildren(...items.map((product) => this.#buildCard(product)));
    this.#healObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  /**
   * @param {Record<string, any>} product
   * @returns {HTMLElement}
   */
  #buildCard(product) {
    const { moneyFormat, currency } = this.dataset;
    const format = moneyFormat || '{{amount}}';
    const currencyCode = currency || 'EUR';

    const image = product.featured_image || product.images?.[0] || '';
    const price = formatMoney(product.price, format, currencyCode);
    const isOnSale = product.compare_at_price > product.price;
    const comparePrice = isOnSale ? formatMoney(product.compare_at_price, format, currencyCode) : '';
    const discount = isOnSale
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : 0;
    const subtitle = product.type || '';

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const hasMultiVariant = variants.length > 1;
    const firstAvailable = variants.find((variant) => variant.available) || variants[0];
    const addLabel = this.dataset.addToCartLabel || 'Ajouter';

    const optionsMarkup = variants
      .map(
        (variant) =>
          `<option value="${variant.id}"${variant.id === firstAvailable?.id ? ' selected' : ''}${
            variant.available ? '' : ' disabled'
          }>${this.#escape(variant.option1 ?? variant.title ?? '')}</option>`
      )
      .join('');

    const card = document.createElement('div');
    card.className = 'cart-reco__card';
    card.dataset.productId = String(product.id);

    card.innerHTML = `
      <a href="${product.url}" class="cart-reco__media">
        <img
          src="${image}"
          alt="${this.#escape(product.title)}"
          class="cart-reco__image"
          loading="lazy"
          width="240"
          height="320"
        >
      </a>
      <div class="cart-reco__actions">
        <div class="cart-reco__actions-inner">
          ${
            hasMultiVariant
              ? `<div class="cart-reco__select-wrap">
                  <select class="cart-reco__select" data-cart-reco-select aria-label="${this.#escape(
                    this.dataset.sizeLabel || 'Taille'
                  )}">${optionsMarkup}</select>
                  <span class="cart-reco__select-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </span>
                </div>`
              : ''
          }
          <button type="button" class="button button-secondary cart-reco__add" data-cart-reco-add data-variant-id="${
            firstAvailable?.id ?? ''
          }">
            <span class="cart-reco__add-label">${this.#escape(addLabel)}</span>
          </button>
        </div>
      </div>
      <div class="cart-reco__info">
        <a href="${product.url}" class="cart-reco__title">${this.#escape(product.title)}</a>
        ${subtitle ? `<p class="cart-reco__type">${this.#escape(subtitle)}</p>` : ''}
        <p class="cart-reco__price">
          ${isOnSale ? `<s class="cart-reco__price-compare">${comparePrice}</s>` : ''}
          <span class="cart-reco__price-current${isOnSale ? ' cart-reco__price-current--sale' : ''}">${price}</span>
          ${isOnSale ? `<span class="cart-reco__price-discount">-${discount}%</span>` : ''}
        </p>
      </div>
    `;

    const select = card.querySelector('[data-cart-reco-select]');
    const addButton = card.querySelector('[data-cart-reco-add]');

    if (select instanceof HTMLSelectElement && addButton) {
      addButton.setAttribute('data-variant-id', select.value);
      select.addEventListener('change', () => addButton.setAttribute('data-variant-id', select.value));
    }

    addButton?.addEventListener('click', (event) => {
      event.preventDefault();
      const variantId = addButton.getAttribute('data-variant-id');
      if (variantId) this.#addVariant(variantId, card);
    });

    return card;
  }

  /**
   * @param {string} variantId
   * @param {HTMLElement} card
   */
  async #addVariant(variantId, card) {
    if (card.classList.contains('cart-reco__card--adding')) return;
    card.classList.add('cart-reco__card--adding');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter((id) => Boolean(id));

    const fetchCfg = fetchConfig('json', {
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchCfg);
      const result = await response.json();

      if (result.status) {
        console.error('Cart recommendations add-to-cart error:', result.message);
        return;
      }

      card.classList.add('cart-reco__card--added');

      document.dispatchEvent(
        new CartAddEvent(result, this.id || 'cart-recommendations', {
          source: 'cart-recommendations',
          itemCount: 1,
          sections: result.sections,
        })
      );

      setTimeout(() => card.classList.remove('cart-reco__card--added'), 1500);
    } catch (error) {
      console.error('Cart recommendations add-to-cart error:', error);
    } finally {
      card.classList.remove('cart-reco__card--adding');
    }
  }

  /**
   * @param {string} value
   */
  #escape(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
